import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Tooltip,
  Chip, Alert, CircularProgress, FormControl, FormControlLabel,
  RadioGroup, Radio, FormGroup, Checkbox, FormLabel,
  useMediaQuery, useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useFirebase } from '../../context/FirebaseContext';
import {
  getServiceApiKeysV2,
  createServiceApiKeyV2,
  updateServiceKeyScopesV2,
  deactivateServiceApiKeyV2,
  deleteServiceApiKeyPermanentV2
} from '../../api/admin';

const CATEGORIES = [
  { value: 'users', label: 'Users' },
  { value: 'config', label: 'Config' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'subscriptions', label: 'Subscriptions' },
  { value: 'oauth', label: 'OAuth' },
  { value: 'errors', label: 'Errors' },
  { value: 'services', label: 'Services' },
];

const ACCESS_LEVELS = [
  { value: 'read', label: 'Read Only', description: 'Can only read data. Safe for monitoring and dashboards.' },
  { value: 'readwrite', label: 'Read & Write', description: 'Can read and modify data. Use for automation scripts.' },
  { value: 'admin', label: 'Full Admin', description: 'Unrestricted access to all admin operations.' },
];

const getScopeColor = (scope) => {
  if (scope === 'admin') return 'success';
  if (scope === 'read') return 'info';
  if (scope === 'write') return 'warning';
  return 'default';
};

const getScopeChipVariant = (scope) => {
  if (['admin', 'read', 'write'].includes(scope)) return 'filled';
  return 'outlined';
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isExpired = (expiresAt) => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

const scopesToAccessLevel = (scopes) => {
  if (!scopes || !Array.isArray(scopes)) return 'read';
  if (scopes.includes('admin')) return 'admin';
  if (scopes.includes('write')) return 'readwrite';
  return 'read';
};

const scopesToCategories = (scopes) => {
  if (!scopes || !Array.isArray(scopes)) return [];
  return scopes.filter(s => !['admin', 'read', 'write'].includes(s));
};

const buildScopes = (accessLevel, selectedCategories) => {
  if (accessLevel === 'admin') return ['admin'];
  const scopes = ['read'];
  if (accessLevel === 'readwrite') scopes.push('write');
  return [...scopes, ...selectedCategories];
};

const ServiceApiKeysTab = () => {
  const { auth } = useFirebase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    accessLevel: 'read',
    categories: [],
    neverExpires: true,
    expiresAt: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Key display dialog state
  const [keyDisplayOpen, setKeyDisplayOpen] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Edit scopes dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({
    accessLevel: 'read',
    categories: [],
  });
  const [editLoading, setEditLoading] = useState(false);

  // Deactivate dialog state
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivatingKey, setDeactivatingKey] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);

  const fetchKeys = useCallback(() => {
    setLoading(true);
    setError(null);
    getServiceApiKeysV2(auth, (data) => {
      setLoading(false);
      if (data?.error) {
        setError(data.error);
        return;
      }
      const list = Array.isArray(data) ? data : Array.isArray(data?.keys) ? data.keys : [];
      setKeys(list);
    });
  }, [auth]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleCreateKey = () => {
    if (!createForm.name.trim()) {
      setCreateError('Name is required.');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    const scopes = buildScopes(createForm.accessLevel, createForm.categories);
    const keyData = {
      name: createForm.name.trim(),
      description: createForm.description.trim() || undefined,
      scopes,
      expiresAt: createForm.neverExpires ? null : createForm.expiresAt || null,
    };

    createServiceApiKeyV2(auth, (data) => {
      setCreateLoading(false);
      if (data?.error || data?.message?.includes('already exists')) {
        setCreateError(data.error || data.message);
        return;
      }
      setNewKeyValue({
        name: createForm.name.trim(),
        apiKey: data.apiKey || data.key || data.keyValue,
      });
      setCreateDialogOpen(false);
      setKeyDisplayOpen(true);
      setCreateForm({
        name: '',
        description: '',
        accessLevel: 'read',
        categories: [],
        neverExpires: true,
        expiresAt: '',
      });
      fetchKeys();
    }, keyData);
  };

  const handleEditScopes = () => {
    if (!editingKey) return;
    setEditLoading(true);

    const scopes = buildScopes(editForm.accessLevel, editForm.categories);

    updateServiceKeyScopesV2(auth, (data) => {
      setEditLoading(false);
      if (data?.error) {
        setError(data.error);
        return;
      }
      setEditDialogOpen(false);
      setEditingKey(null);
      setSuccessMessage('Scopes updated successfully.');
      fetchKeys();
    }, editingKey.id, scopes);
  };

  const handleDeactivate = () => {
    if (!deactivatingKey) return;
    deactivateServiceApiKeyV2(auth, (data) => {
      if (data?.error) {
        setError(data.error);
        return;
      }
      setDeactivateDialogOpen(false);
      setDeactivatingKey(null);
      setSuccessMessage('API key deactivated.');
      fetchKeys();
    }, deactivatingKey.id);
  };

  const handleDeletePermanent = () => {
    if (!deletingKey) return;
    deleteServiceApiKeyPermanentV2(auth, (data) => {
      if (data?.error) {
        setError(data.error);
        return;
      }
      setDeleteDialogOpen(false);
      setDeletingKey(null);
      setSuccessMessage('API key permanently deleted.');
      fetchKeys();
    }, deletingKey.id);
  };

  const openEditDialog = (key) => {
    const scopes = key.scopes || [];
    setEditingKey(key);
    setEditForm({
      accessLevel: scopesToAccessLevel(scopes),
      categories: scopesToCategories(scopes),
    });
    setEditDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    });
  };

  const handleCategoryToggle = (category, form, setForm) => {
    const current = form.categories;
    const updated = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    setForm({ ...form, categories: updated });
  };

  const renderScopeChips = (scopes) => {
    if (!scopes || !Array.isArray(scopes) || scopes.length === 0) {
      return <Typography variant="caption" color="text.secondary">None</Typography>;
    }
    return scopes.map((scope, idx) => (
      <Chip
        key={idx}
        label={scope}
        size="small"
        color={getScopeColor(scope)}
        variant={getScopeChipVariant(scope)}
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    ));
  };

  const renderAccessLevelRadios = (form, setForm) => (
    <FormControl component="fieldset" sx={{ mt: 2, mb: 1, width: '100%' }}>
      <FormLabel component="legend">Access Level</FormLabel>
      <RadioGroup
        value={form.accessLevel}
        onChange={(e) => setForm({ ...form, accessLevel: e.target.value, categories: [] })}
      >
        {ACCESS_LEVELS.map((level) => (
          <FormControlLabel
            key={level.value}
            value={level.value}
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>{level.label}</Typography>
                <Typography variant="caption" color="text.secondary">{level.description}</Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 0.5 }}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );

  const renderCategoryCheckboxes = (form, setForm) => {
    if (form.accessLevel === 'admin') return null;
    return (
      <FormControl component="fieldset" sx={{ mt: 1, mb: 1, width: '100%' }}>
        <FormLabel component="legend">Allowed Categories</FormLabel>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Restrict access to specific admin sections. Leave empty to allow all categories.
        </Typography>
        <FormGroup row>
          {CATEGORIES.map((cat) => (
            <FormControlLabel
              key={cat.value}
              control={
                <Checkbox
                  checked={form.categories.includes(cat.value)}
                  onChange={() => handleCategoryToggle(cat.value, form, setForm)}
                  size="small"
                />
              }
              label={cat.label}
            />
          ))}
        </FormGroup>
      </FormControl>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Service API Keys</Typography>
        <Box>
          <Button startIcon={<RefreshIcon />} onClick={fetchKeys} sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New API Key
          </Button>
        </Box>
      </Box>

      {/* Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Service API keys provide programmatic access to admin endpoints without user authentication.
        Use them for monitoring dashboards, automation scripts, and internal services.
        Keys are shown only once at creation time - store them securely.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <div className="admin-cards-mobile">
          {keys.length === 0 ? (
            <Typography color="text.secondary" py={4} textAlign="center">
              No service API keys configured. Create one to enable programmatic admin access.
            </Typography>
          ) : (
            keys.map((key) => {
              const expired = isExpired(key.expiresAt);
              const inactive = !key.active && !key.isActive;
              return (
                <div className="admin-mobile-card" key={key.id} style={{ opacity: inactive ? 0.6 : 1 }}>
                  <div className="admin-mobile-card-header">
                    <Typography fontWeight={600}>{key.name}</Typography>
                    <Chip
                      label={inactive ? 'Inactive' : expired ? 'Expired' : 'Active'}
                      size="small"
                      color={inactive ? 'default' : expired ? 'error' : 'success'}
                      variant={inactive ? 'outlined' : 'filled'}
                    />
                  </div>
                  <div className="admin-mobile-card-body">
                    <div className="admin-mobile-card-row">
                      <span className="admin-mobile-card-row-label">Description</span>
                      <span className="admin-mobile-card-row-value">
                        {key.description || '-'}
                      </span>
                    </div>
                    <div className="admin-mobile-card-row">
                      <span className="admin-mobile-card-row-label">Scopes</span>
                      <div className="admin-mobile-card-chips">
                        {renderScopeChips(key.scopes)}
                      </div>
                    </div>
                    <div className="admin-mobile-card-row">
                      <span className="admin-mobile-card-row-label">Created</span>
                      <span className="admin-mobile-card-row-value">
                        {formatDate(key.createdAt)}
                      </span>
                    </div>
                    <div className="admin-mobile-card-row">
                      <span className="admin-mobile-card-row-label">Last Used</span>
                      <span className="admin-mobile-card-row-value">
                        {formatDate(key.lastUsedAt)}
                      </span>
                    </div>
                    <div className="admin-mobile-card-row">
                      <span className="admin-mobile-card-row-label">Expires</span>
                      <span className="admin-mobile-card-row-value">
                        <Typography
                          component="span"
                          variant="body2"
                          color={expired ? 'error.main' : 'text.primary'}
                          fontWeight={expired ? 600 : 400}
                        >
                          {key.expiresAt ? formatDate(key.expiresAt) : 'Never'}
                          {expired && ' (Expired)'}
                        </Typography>
                      </span>
                    </div>
                  </div>
                  <div className="admin-mobile-card-actions">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openEditDialog(key)}
                      disabled={inactive}
                    >
                      Edit Scopes
                    </Button>
                    {!inactive && (
                      <Button
                        size="small"
                        color="warning"
                        startIcon={<BlockIcon />}
                        onClick={() => {
                          setDeactivatingKey(key);
                          setDeactivateDialogOpen(true);
                        }}
                      >
                        Deactivate
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setDeletingKey(key);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Keys Table (Desktop) */}
      <div className="admin-table-desktop">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" py={4}>
                      No service API keys configured. Create one to enable programmatic admin access.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                keys.map((key) => {
                  const expired = isExpired(key.expiresAt);
                  const inactive = !key.active && !key.isActive;
                  return (
                    <TableRow
                      key={key.id}
                      sx={{ opacity: inactive ? 0.6 : 1 }}
                    >
                      <TableCell>
                        <Typography fontWeight={500}>{key.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={key.description || ''} placement="top">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {key.description || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                          {renderScopeChips(key.scopes)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(key.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={key.lastUsedAt ? 'text.primary' : 'text.secondary'}>
                          {formatDate(key.lastUsedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={expired ? 'error.main' : 'text.primary'}
                          fontWeight={expired ? 600 : 400}
                        >
                          {key.expiresAt ? formatDate(key.expiresAt) : 'Never'}
                          {expired && ' (Expired)'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inactive ? 'Inactive' : expired ? 'Expired' : 'Active'}
                          size="small"
                          color={inactive ? 'default' : expired ? 'error' : 'success'}
                          variant={inactive ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Edit Scopes">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(key)}
                              color="primary"
                              disabled={inactive}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!inactive && (
                            <Tooltip title="Deactivate Key">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeactivatingKey(key);
                                  setDeactivateDialogOpen(true);
                                }}
                                color="warning"
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Permanently">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDeletingKey(key);
                                setDeleteDialogOpen(true);
                              }}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Create API Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Service API Key</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            placeholder="e.g., Grafana Monitoring, CI Pipeline"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            margin="normal"
            required
            helperText="A descriptive name to identify this key's purpose."
          />
          <TextField
            fullWidth
            label="Description"
            placeholder="Optional description of what this key is used for"
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />

          {renderAccessLevelRadios(createForm, setCreateForm)}
          {renderCategoryCheckboxes(createForm, setCreateForm)}

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createForm.neverExpires}
                  onChange={(e) => setCreateForm({ ...createForm, neverExpires: e.target.checked, expiresAt: '' })}
                />
              }
              label="Never expires"
            />
            {!createForm.neverExpires && (
              <TextField
                fullWidth
                label="Expires At"
                type="datetime-local"
                value={createForm.expiresAt}
                onChange={(e) => setCreateForm({ ...createForm, expiresAt: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                helperText="The key will stop working after this date."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateKey}
            variant="contained"
            disabled={!createForm.name.trim() || createLoading}
          >
            {createLoading ? <CircularProgress size={24} /> : 'Create Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Key Display Dialog (shown once after creation) */}
      <Dialog
        open={keyDisplayOpen}
        onClose={() => setKeyDisplayOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Copy this API key now. It will not be shown again. If you lose it, you will need to
            create a new key.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>Key Name</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{newKeyValue?.name}</Typography>

          <Typography variant="subtitle2" gutterBottom>API Key</Typography>
          <Paper
            sx={{
              p: 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'warning.light',
              border: '2px solid',
              borderColor: 'warning.main',
            }}
          >
            <Typography
              component="code"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                wordBreak: 'break-all',
                flex: 1,
                mr: 1,
              }}
            >
              {newKeyValue?.apiKey}
            </Typography>
            <Tooltip title={keyCopied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton onClick={() => copyToClipboard(newKeyValue?.apiKey)}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Paper>

          <Typography variant="subtitle2" gutterBottom>Usage</Typography>
          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography
              component="code"
              sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            >
              X-Admin-Api-Key: {newKeyValue?.apiKey || '<your-key>'}
            </Typography>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Include this header in your HTTP requests to authenticate with admin endpoints.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyDisplayOpen(false)} variant="contained">
            I've Saved the Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Scopes Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Scopes</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Update the access scopes for <strong>{editingKey?.name}</strong>.
            Changes take effect immediately.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>Key ID</Typography>
          <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <code>{editingKey?.id}</code>
          </Paper>

          {renderAccessLevelRadios(editForm, setEditForm)}
          {renderCategoryCheckboxes(editForm, setEditForm)}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Resulting Scopes</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {renderScopeChips(buildScopes(editForm.accessLevel, editForm.categories))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditScopes}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save Scopes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
      >
        <DialogTitle>Deactivate API Key?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately revoke access for any service using this key.
          </Alert>
          <Typography>
            Are you sure you want to deactivate <strong>{deactivatingKey?.name}</strong>?
            The key will remain in the system but will no longer authenticate requests.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeactivate} color="warning" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Permanently Delete API Key?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. The key and all its usage history will be permanently removed.
          </Alert>
          <Typography>
            Are you sure you want to permanently delete <strong>{deletingKey?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            If the key is still active, any service using it will immediately lose access.
            Consider deactivating it first if you may need to audit usage later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePermanent} color="error" variant="contained">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceApiKeysTab;
